import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import {
    Users, Mountain, MessageCircle, Heart, Store, Flag, Share2, TrendingUp,
    Megaphone, ShieldAlert, ArrowRight,
} from "lucide-react";

export default function OverviewTab({ stats, onJump }) {
    const [analytics, setAnalytics] = useState(null);
    const [analyticsWindow, setAnalyticsWindow] = useState("7d");
    const [recentUsers, setRecentUsers] = useState([]);
    const [recentReports, setRecentReports] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/share-analytics", {
                    params: { since: analyticsWindow },
                });
                setAnalytics(data);
            } catch {
                setAnalytics(null);
            }
        })();
    }, [analyticsWindow]);

    useEffect(() => {
        (async () => {
            try {
                const [u, r] = await Promise.all([
                    api.get("/admin/users", { params: { sort: "newest", page_size: 5 } }),
                    api.get("/admin/reports", { params: { page_size: 5 } }),
                ]);
                setRecentUsers(u.data.items || []);
                setRecentReports(r.data.items || []);
            } catch { /* ignore */ }
        })();
    }, []);

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatTile icon={Users} label="Users" value={stats?.users} />
                <StatTile icon={Mountain} label="Adventures" value={stats?.adventures} />
                <StatTile icon={MessageCircle} label="Comments" value={stats?.comments} />
                <StatTile icon={Heart} label="Likes" value={stats?.likes} />
                <StatTile icon={Store} label="Listings" value={stats?.listings} />
                <StatTile label="Free tier" value={stats?.tiers?.free} />
                <StatTile label="Adventure" value={stats?.tiers?.adventure} />
                <StatTile label="Premium" value={stats?.tiers?.premium} highlight />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <AlertTile
                    label="Open reports"
                    value={stats?.reports?.open || 0}
                    danger={(stats?.reports?.open || 0) > 0}
                    icon={Flag}
                    onClick={() => onJump("reports")}
                    testid="overview-jump-reports"
                />
                <AlertTile
                    label="Suspended users"
                    value={stats?.suspended_users || 0}
                    danger={(stats?.suspended_users || 0) > 0}
                    icon={ShieldAlert}
                    onClick={() => onJump("users")}
                    testid="overview-jump-suspended"
                />
                <AlertTile
                    label="Active campaigns"
                    value={stats?.ads?.active || 0}
                    icon={Megaphone}
                    onClick={() => onJump("advertising")}
                    testid="overview-jump-ads"
                />
            </div>

            <section className="bg-snowcap border border-ridge-line rounded-lg p-6 min-w-0">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <h2 className="font-display font-bold text-xl text-midnight-pine flex items-center gap-2">
                        <Share2 className="w-5 h-5" /> Share analytics
                    </h2>
                    <div className="flex items-center gap-3">
                        <div className="inline-flex bg-alpine-mist rounded-md p-1 border border-ridge-line">
                            {["24h", "7d", "30d", "all"].map((w) => (
                                <button
                                    key={w}
                                    data-testid={`analytics-window-${w}`}
                                    onClick={() => setAnalyticsWindow(w)}
                                    className={`px-3 py-1 rounded text-xs font-bold uppercase tracking-widest transition-all ${
                                        analyticsWindow === w
                                            ? "bg-midnight-pine text-white"
                                            : "text-slate-gray hover:text-granite"
                                    }`}
                                >
                                    {w === "all" ? "All time" : w}
                                </button>
                            ))}
                        </div>
                        {analytics?.totals && (
                            <div className="text-xs text-slate-gray font-semibold whitespace-nowrap">
                                <span className="text-granite font-bold">{analytics.totals.humans}</span> humans ·{" "}
                                <span className="text-slate-gray">{analytics.totals.bots} bots</span>
                            </div>
                        )}
                    </div>
                </div>
                {!analytics || analytics.totals.humans === 0 ? (
                    <p className="text-slate-gray text-sm">
                        No share traffic yet. Drop a link in the wild — Reddit, X, WhatsApp — and the UTM-tagged hits show up here within seconds.
                    </p>
                ) : (
                    <div className="grid md:grid-cols-2 gap-6">
                        <AnalyticsColumn title="Top UTM sources" rows={analytics.sources} labelKey="source" testPrefix="share-source" />
                        <AnalyticsColumn title="By kind" rows={analytics.kinds} labelKey="kind" testPrefix="share-kind" capitalize />
                        <AnalyticsColumn title="Top campaigns" rows={analytics.campaigns} labelKey="campaign" testPrefix="share-campaign" empty="No UTM campaigns recorded yet." />
                        <AnalyticsColumn
                            title="Top shared items"
                            rows={analytics.top_targets}
                            labelKey="title"
                            metaKey="kind"
                            testPrefix="share-target"
                            icon={<TrendingUp className="w-4 h-4 text-ember-orange" />}
                        />
                    </div>
                )}
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <section className="bg-snowcap border border-ridge-line rounded-lg p-6 min-w-0">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-display font-bold text-xl text-midnight-pine">Recent users</h2>
                        <button
                            data-testid="overview-view-all-users"
                            onClick={() => onJump("users")}
                            className="text-sm font-bold text-ember-orange hover:underline flex items-center gap-1"
                        >
                            View all <ArrowRight className="w-3 h-3" />
                        </button>
                    </div>
                    {recentUsers.length === 0 ? (
                        <p className="text-slate-gray text-sm">No users yet.</p>
                    ) : (
                        <ul className="divide-y divide-ridge-line">
                            {recentUsers.map((u) => (
                                <li
                                    key={u.user_id}
                                    data-testid={`overview-recent-user-${u.user_id}`}
                                    className="flex items-center justify-between py-2.5"
                                >
                                    <div className="min-w-0">
                                        <Link
                                            to={`/profile/${u.user_id}`}
                                            className="font-semibold text-granite hover:text-ember-orange truncate block"
                                        >
                                            {u.name}
                                        </Link>
                                        <div className="text-xs text-slate-gray truncate">{u.email}</div>
                                    </div>
                                    <span className="text-xs font-bold uppercase tracking-widest text-slate-gray capitalize shrink-0">
                                        {u.tier}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section className="bg-snowcap border border-ridge-line rounded-lg p-6 min-w-0">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-display font-bold text-xl text-midnight-pine flex items-center gap-2">
                            <Flag className="w-5 h-5" /> Recent reports
                        </h2>
                        <button
                            data-testid="overview-view-all-reports"
                            onClick={() => onJump("reports")}
                            className="text-sm font-bold text-ember-orange hover:underline flex items-center gap-1"
                        >
                            View all <ArrowRight className="w-3 h-3" />
                        </button>
                    </div>
                    {recentReports.length === 0 ? (
                        <p className="text-slate-gray text-sm">No reports yet.</p>
                    ) : (
                        <ul className="divide-y divide-ridge-line">
                            {recentReports.map((r) => (
                                <li
                                    key={r.id}
                                    data-testid={`overview-recent-report-${r.id}`}
                                    className="py-2.5"
                                >
                                    <div className="flex items-center justify-between mb-0.5">
                                        <span className="font-semibold text-sm capitalize">
                                            {r.target_type}: {r.target_id}
                                        </span>
                                        <StatusPill status={r.status} />
                                    </div>
                                    <p className="text-xs text-slate-gray truncate">{r.reason}</p>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>
        </div>
    );
}

function StatTile({ icon: Icon, label, value, highlight }) {
    return (
        <div
            data-testid={`stat-${label?.toLowerCase().replace(/\s+/g, "-")}`}
            className={`rounded-lg p-4 border ${
                highlight ? "bg-midnight-pine text-white border-deep-evergreen" : "bg-snowcap border-ridge-line"
            }`}
        >
            {Icon && <Icon className={`w-4 h-4 mb-2 ${highlight ? "text-ember-orange" : "text-slate-gray"}`} />}
            <div className="font-display font-black text-2xl">{value ?? 0}</div>
            <div className="text-[10px] font-bold uppercase tracking-widest opacity-70 mt-0.5">{label}</div>
        </div>
    );
}

function AlertTile({ icon: Icon, label, value, danger, onClick, testid }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className={`text-left rounded-lg p-4 border transition-all hover:shadow-md ${
                danger
                    ? "bg-ember-orange/10 border-ember-orange/40 hover:border-ember-orange"
                    : "bg-snowcap border-ridge-line hover:border-midnight-pine"
            }`}
        >
            <div className="flex items-center justify-between mb-2">
                {Icon && <Icon className={`w-4 h-4 ${danger ? "text-ember-orange" : "text-slate-gray"}`} />}
                <ArrowRight className="w-3 h-3 text-slate-gray" />
            </div>
            <div className="font-display font-black text-2xl text-midnight-pine">{value}</div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-gray mt-0.5">{label}</div>
        </button>
    );
}

function StatusPill({ status }) {
    const styles = {
        open: "bg-ember-orange/15 text-ember-dark",
        in_review: "bg-trail-tan/40 text-granite",
        resolved: "bg-deep-evergreen/15 text-deep-evergreen",
        dismissed: "bg-alpine-mist text-slate-gray",
    };
    return (
        <span
            className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                styles[status] || styles.open
            }`}
        >
            {(status || "open").replace("_", " ")}
        </span>
    );
}

function AnalyticsColumn({ title, rows, labelKey, metaKey, testPrefix, icon, empty, capitalize }) {
    const data = rows || [];
    const max = Math.max(1, ...data.map((r) => r.count || 0));
    return (
        <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-gray mb-3">
                {icon}
                {title}
            </div>
            {data.length === 0 ? (
                <p className="text-sm text-slate-gray italic">{empty || "No data yet."}</p>
            ) : (
                <ul className="space-y-2">
                    {data.map((row, i) => {
                        const label = row[labelKey] || "—";
                        const pct = Math.round((row.count / max) * 100);
                        return (
                            <li key={i} data-testid={`${testPrefix}-row-${i}`} className="space-y-1">
                                <div className="flex items-center justify-between text-sm">
                                    <span className={`font-semibold text-granite truncate ${capitalize ? "capitalize" : ""}`}>
                                        {label}
                                        {metaKey && row[metaKey] && (
                                            <span className="ml-2 text-[10px] font-bold uppercase tracking-widest text-slate-gray">
                                                {row[metaKey]}
                                            </span>
                                        )}
                                    </span>
                                    <span className="font-display font-black text-midnight-pine">{row.count}</span>
                                </div>
                                <div className="h-1.5 bg-alpine-mist rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-ember-orange rounded-full transition-all"
                                        style={{ width: `${pct}%` }}
                                    />
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
